/**
 * Post-Article Survey — Likert items per article, organized by cue class.
 *
 * Original Study-1 items KEPT verbatim for continuity (SEC1-3, SL1-3, AQ1-2,
 * BE1-2, IS1-2, CB1-2) + strengthening additions (BE3, IS3, PF1-3, PFeat1-3,
 * SL3R, ATTN1). Nothing is dropped; SL3R is an added rephrase of SL3, not a
 * replacement of it. Construct → cue-class mapping lives in docs/STUDY2_CODEBOOK.md:
 *   SEC = Trust cue · {SL, SL3R, PF} = Affective cue (Processing Fluency) ·
 *   AQ = Epistemic cue · BE = Belief (mediator) · IS = Sharing intention (outcome) ·
 *   CB2 = Stance Alignment, CB1 = Perceived Importance (controls) ·
 *   PFeat1-3 = perceptual-feature indicators (coherence / source plausibility / prior-knowledge fit) ·
 *   MEM_V/MEM_G = FTT verbatim/gist memory probe.
 * Plus a required structured CUE-ATTRIBUTION matrix (CA_* — how much each cue class
 * drove belief, 1-5), an optional free-text, a self-reported processing mode, and an
 * instructed-response attention check.
 *
 * UI matches PostTaskBattery's mature styling (same Likert scales, colors, spacing).
 */

import React, { useState } from 'react';

interface PostArticleSurveyProps {
  articleId: string;
  onComplete: (responses: PostArticleSurveyData) => void;
}

export interface PostArticleSurveyData {
  articleId: string;
  SEC1: number; SEC2: number; SEC3: number;
  SL1: number; SL2: number; SL3: number;
  // SL3R: rephrased replacement for SL3 (reviewers flagged SL3's "all the
  // information I need" as overlapping Argument Quality). SL3 is KEPT verbatim
  // for Study-1 continuity; SL3R reframes the item as subjective processing ease.
  // Both load on the Affective cue (Processing Fluency); see STUDY2_CODEBOOK.
  SL3R: number;
  AQ1: number; AQ2: number;
  BE1: number; BE2: number; BE3: number;
  // FTT memory probe — verbatim (MEM_V) vs gist (MEM_G) trace strength.
  MEM_V: number; MEM_G: number;
  IS1: number; IS2: number; IS3: number;
  CB1: number; CB2: number;
  PF1: number; PF2: number; PF3: number;
  PFeat1: number; PFeat2: number; PFeat3: number;
  ATTN1: number; attentionPassed: boolean; // instructed-response attention check
  // Full attention-check audit trail (never used to drop data — only to FLAG):
  // what value was instructed (`attentionExpected`/`attentionLabel`) and where the
  // item was placed (`attentionSection`/`attentionRow`), so any pass/fail is
  // reproducible from the raw answer (`ATTN1`) alone.
  attentionExpected: number; attentionLabel: string;
  attentionSection: number; attentionRow: number;
  processingMode: string; // 'gist' | 'verbatim' | 's1_to_s2' | 's2_to_s1'
  // Structured cue-attribution: how much each cue class influenced belief (1=Not
  // at all … 5=A great deal). Maps to the typology: source/endorser=trust,
  // claims=epistemic, presentation=affective, prior=stance. Triangulates the
  // behavioral mediation. openEndedResponse is an optional free-text, retained.
  cueAttribution: { source: number; endorser: number; claims: number; presentation: number; prior: number };
  // Manipulation checks: perceived political lean (1=strongly liberal … 7=strongly
  // conservative) and whether the endorser was noticed ('saw' | 'no' | 'unsure').
  partisanLean: number;
  endorserNoticed: string;
  // Prior-exposure control (illusory-truth): asked LAST so it cannot prime the
  // reading or the belief ratings. 'no' | 'yes' | 'unsure'. Modeled as a covariate
  // on belief (a-path) with a robustness check excluding 'yes'.
  priorExposure: string;
  sectionOrder: number[]; // counterbalanced order of Likert sections (procedural CMB)
  openEndedResponse: string;
  surveyStartTime: number;
  surveyCompletionTime: number;
}

// Matches PostTaskBattery's Likert7
const LIKERT = [
  { value: 1, label: "Strongly\nDisagree" },
  { value: 2, label: "Disagree" },
  { value: 3, label: "Somewhat\nDisagree" },
  { value: 4, label: "Neither Agree\nnor Disagree" },
  { value: 5, label: "Somewhat\nAgree" },
  { value: 6, label: "Agree" },
  { value: 7, label: "Strongly\nAgree" },
];

// Styling matches PostTaskBattery exactly
const S = {
  container: { maxWidth: '720px', margin: '0 auto', padding: '24px' } as React.CSSProperties,
  section: { marginBottom: '32px' } as React.CSSProperties,
  q: { fontSize: '15px', fontWeight: 600, color: '#2d3748', marginBottom: '12px', lineHeight: 1.5 } as React.CSSProperties,
  qLight: { fontSize: '14px', fontWeight: 400, color: '#4a5568', marginBottom: '12px', lineHeight: 1.5 } as React.CSSProperties,
  likertGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '6px',
    padding: '16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '2px solid #e9ecef',
    marginBottom: '20px',
  } as React.CSSProperties,
  likertCard: (active: boolean) => ({
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '6px',
    padding: '10px 4px',
    backgroundColor: active ? '#e3f2fd' : 'white',
    borderRadius: '6px',
    border: active ? '2px solid #0066cc' : '1px solid #dee2e6',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    minHeight: '60px',
    justifyContent: 'center',
  } as React.CSSProperties),
  likertLabel: {
    fontSize: '11px',
    textAlign: 'center' as const,
    color: '#6c757d',
    fontWeight: 500,
    lineHeight: 1.3,
    whiteSpace: 'pre-line' as const,
  } as React.CSSProperties,
  bar: { width: '100%', height: '6px', backgroundColor: '#e9ecef', borderRadius: '3px', marginBottom: '24px', overflow: 'hidden' } as React.CSSProperties,
  btn: { padding: '14px 32px', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' } as React.CSSProperties,
};

// Survey sections — 3 pages, ~7 items each
// DV FIRST — sharing instinct before belief judgment (more natural, like a real feed)
// Separate pages so participants don't anchor one on the other
type SurveyItem = { id: string; text: string; reverseCode?: boolean; attention?: number };
const SECTIONS: { title: string; items: SurveyItem[] }[] = [
  {
    title: 'Sharing',
    items: [
      { id: 'IS1', text: 'I will share this article online from my social media account' },
      { id: 'IS2', text: 'I will continue to share similar articles online in the future' },
      { id: 'IS3', text: 'I would recommend this article to someone interested in this topic' },
    ],
  },
  {
    title: 'Your Belief',
    items: [
      { id: 'BE1', text: 'How believable do you find this article?' },
      { id: 'BE2', text: 'Overall, I find this article highly improbable', reverseCode: true },
      { id: 'BE3', text: 'I think the information in this article is accurate' },
    ],
  },
  {
    // Gist vs verbatim memory (FTT trace strength), self-reported right after
    // reading. MEM_V taps the verbatim trace (precise surface detail); MEM_G taps
    // the gist trace (bottom-line meaning). Their relative strength is the core
    // FTT signal — see STUDY2_CODEBOOK.
    title: 'What You Remember',
    items: [
      { id: 'MEM_V', text: 'I could recall specific details, numbers, or exact wording from this article' },
      { id: 'MEM_G', text: 'I could recall the overall point or bottom-line message of this article, even without the details' },
    ],
  },
  {
    title: 'Your Impressions',
    items: [
      { id: 'SEC1', text: 'The website publishing the article is credible' },
      { id: 'SEC2', text: 'The person who endorsed this article is trustworthy' },
      { id: 'SEC3', text: 'The person who endorsed this article is credible' },
      { id: 'SL1', text: 'This news web page looks professional and neat' },
      { id: 'SL2', text: 'This news web page provides content I like / enjoy reading' },
      { id: 'SL3', text: 'This news web page provides me with all the information I need related to the topic' },
      // SL3R — rephrased replacement for SL3, reframed toward subjective
      // processing ease (Affective cue) rather than informational adequacy.
      { id: 'SL3R', text: 'I could take in this web page and grasp its point with little effort' },
      { id: 'PF1', text: 'This article was easy to read and follow' },
      { id: 'PF2', text: 'I could quickly grasp the main point of this article' },
      { id: 'PF3', text: 'The presentation of this article felt smooth and effortless' },
    ],
  },
  {
    title: 'About the Content',
    items: [
      { id: 'AQ1', text: 'The information provided in the article feels complete' },
      { id: 'AQ2', text: 'The information provided in the article is consistent' },
      { id: 'PFeat1', text: 'The claims in this article were internally consistent with each other' },
      { id: 'PFeat2', text: 'The source of this article seemed like a real and established news organization' },
      { id: 'PFeat3', text: 'The information in this article matched what I already know about this topic' },
      { id: 'CB1', text: 'I find the information in this article to be important' },
      // Stance Alignment — shown to participants as a standard 7-point Likert (1=Strongly
      // Disagree … 7=Strongly Agree). Recode to the -3..+3 analysis scale as CB2-4.
      { id: 'CB2', text: 'I agree with the overall stance of this article' },
    ],
  },
];

// Instructed-response attention check, randomized per article so its position and
// target answer can't be learned/auto-answered. Built fresh on each survey mount.
const ATTN_TARGETS = [
  { value: 2, label: 'Disagree' },
  { value: 3, label: 'Somewhat Disagree' },
  { value: 5, label: 'Somewhat Agree' },
  { value: 6, label: 'Agree' },
];
const buildRandomAttention = () => {
  const t = ATTN_TARGETS[Math.floor(Math.random() * ATTN_TARGETS.length)];
  // Pick a random Likert section and a random row within it to insert the check.
  const sectionIdx = Math.floor(Math.random() * SECTIONS.length);
  const rowIdx = Math.floor(Math.random() * (SECTIONS[sectionIdx].items.length + 1));
  return {
    sectionIdx, rowIdx, expected: t.value, label: t.label,
    item: { id: 'ATTN1', text: `To show you are reading carefully, please select "${t.label}" for this question`, attention: t.value } as SurveyItem,
  };
};

// Cue-attribution: self-reported influence of each cue class on belief. Rows map
// 1:1 to the cue typology (trust=source/endorser, epistemic=claims, affective=
// presentation, stance=prior), so these triangulate the behavioral mediation.
const INFLUENCE = [
  { value: 1, label: 'Not at\nall' },
  { value: 2, label: 'A\nlittle' },
  { value: 3, label: 'Somewhat' },
  { value: 4, label: 'Quite\na bit' },
  { value: 5, label: 'A great\ndeal' },
];
const CUE_ATTRIBUTION: SurveyItem[] = [
  { id: 'CA_source', text: 'The source or news outlet it came from' },
  { id: 'CA_endorser', text: 'The person who shared or endorsed it' },
  { id: 'CA_claims', text: 'The specific claims or evidence in the article' },
  { id: 'CA_presentation', text: 'The writing, layout, or overall tone' },
  { id: 'CA_prior', text: 'Whether it matched what I already believed' },
];

// Manipulation checks (placed AFTER the main DVs so they don't prime scrutiny).
// MC_partisan: bipolar perceived political lean — validates the assigned left/right
// manipulation and underpins the stance/motivational-rival analysis. Endorser
// recognition (categorical) confirms the endorser was noticed (addresses Reviewer 3).
const POLITICAL = [
  { value: 1, label: 'Strongly\nliberal' },
  { value: 2, label: 'Liberal' },
  { value: 3, label: 'Slightly\nliberal' },
  { value: 4, label: 'Neutral /\nmixed' },
  { value: 5, label: 'Slightly\nconservative' },
  { value: 6, label: 'Conservative' },
  { value: 7, label: 'Strongly\nconservative' },
];
const PARTISAN_ITEM: SurveyItem = { id: 'MC_partisan', text: 'Politically, this article leans:' };
const ENDORSER_NOTICED_OPTIONS = [
  { value: 'saw', label: 'Yes — it included a social-media post / endorsement from a public figure' },
  { value: 'no', label: 'No — there was no such post' },
  { value: 'unsure', label: "I don't remember" },
];
// Prior exposure is to the STORY/CLAIM (from any source), not this exact article —
// illusory truth tracks the claim, and these are synthetic replicas nobody has
// seen verbatim.
const PRIOR_EXPOSURE_OPTIONS = [
  { value: 'no', label: 'No — this is the first time' },
  { value: 'yes', label: 'Yes — I had seen or heard about it before' },
  { value: 'unsure', label: 'Not sure' },
];

const PostArticleSurvey: React.FC<PostArticleSurveyProps> = ({ articleId, onComplete }) => {
  const [responses, setResponses] = useState<Record<string, number>>({});
  const [processingMode, setProcessingMode] = useState<string>('');
  const [openEnded, setOpenEnded] = useState('');
  const [currentSection, setCurrentSection] = useState(0);
  const [surveyStartTime] = useState(Date.now());
  // Random attention check, fixed for this article's survey instance.
  const [attn] = useState(buildRandomAttention);

  // Sections with the attention check spliced into its randomized slot.
  const builtSections = SECTIONS.map((sec, i) =>
    i === attn.sectionIdx
      ? { ...sec, items: [...sec.items.slice(0, attn.rowIdx), attn.item, ...sec.items.slice(attn.rowIdx)] }
      : sec
  );
  // COUNTERBALANCE the Likert section order per participant (procedural CMB remedy;
  // Podsakoff et al. 2024) — fixed once per article instance. The attention check
  // travels with its section, and `sectionOrder` is stored for analysis.
  const [sectionOrder] = useState<number[]>(() => {
    const idx = builtSections.map((_, i) => i);
    for (let i = idx.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [idx[i], idx[j]] = [idx[j], idx[i]]; }
    return idx;
  });
  const displaySections = sectionOrder.map(i => builtSections[i]);

  // Endorser-recognition manipulation check (categorical).
  const [endorserNoticed, setEndorserNoticed] = useState<string>('');
  // Prior-exposure control (asked last).
  const [priorExposure, setPriorExposure] = useState<string>('');

  // Cue-attribution + manipulation checks are required before submit.
  const cueAttributionComplete = CUE_ATTRIBUTION.every(i => responses[i.id] != null);
  const manipChecksComplete = responses[PARTISAN_ITEM.id] != null && !!endorserNoticed && !!priorExposure;

  const handleResponse = (id: string, value: number) => {
    setResponses(prev => ({ ...prev, [id]: value }));
  };

  const totalSections = displaySections.length + 1; // +1 for final page
  const isLikertPage = currentSection < displaySections.length;
  const section = displaySections[currentSection];
  const sectionComplete = section ? section.items.every(item => responses[item.id] != null) : true;
  const progress = ((currentSection + 1) / totalSections) * 100;

  const handleSubmit = () => {
    if (!processingMode || !cueAttributionComplete || !manipChecksComplete) return;

    const data: PostArticleSurveyData = {
      articleId,
      SEC1: responses.SEC1, SEC2: responses.SEC2, SEC3: responses.SEC3,
      SL1: responses.SL1, SL2: responses.SL2, SL3: responses.SL3, SL3R: responses.SL3R,
      AQ1: responses.AQ1, AQ2: responses.AQ2,
      BE1: responses.BE1, BE2: responses.BE2, BE3: responses.BE3,
      MEM_V: responses.MEM_V, MEM_G: responses.MEM_G,
      IS1: responses.IS1, IS2: responses.IS2, IS3: responses.IS3,
      CB1: responses.CB1, CB2: responses.CB2,
      PF1: responses.PF1, PF2: responses.PF2, PF3: responses.PF3,
      PFeat1: responses.PFeat1, PFeat2: responses.PFeat2, PFeat3: responses.PFeat3,
      ATTN1: responses.ATTN1, attentionPassed: responses.ATTN1 === attn.expected,
      attentionExpected: attn.expected, attentionLabel: attn.label,
      attentionSection: attn.sectionIdx, attentionRow: attn.rowIdx,
      processingMode,
      cueAttribution: {
        source: responses.CA_source, endorser: responses.CA_endorser,
        claims: responses.CA_claims, presentation: responses.CA_presentation,
        prior: responses.CA_prior,
      },
      partisanLean: responses.MC_partisan,
      endorserNoticed,
      priorExposure,
      sectionOrder, // counterbalanced section order shown to this participant
      openEndedResponse: openEnded,
      surveyStartTime,
      surveyCompletionTime: Date.now(),
    };
    onComplete(data);
  };

  // Qualtrics-style matrix: one shared header row of scale labels, then each item
  // as a compact zebra-striped row. Breaks the monotony of a full card-grid per
  // item and reads faster. (reverseCode is scoring-only — never surfaced.)
  const renderMatrix = (items: SurveyItem[], scale: { value: number; label: string }[] = LIKERT) => {
    const GRID_COLS = `minmax(150px, 1.5fr) repeat(${scale.length}, minmax(36px, 1fr))`;
    return (
    <div role="group" style={{ border: '1px solid #e3e6ea', borderRadius: '10px', overflow: 'hidden', marginBottom: '8px' }}>
      {/* Header row of scale labels */}
      <div style={{ display: 'grid', gridTemplateColumns: GRID_COLS, alignItems: 'end', background: '#eef1f4', borderBottom: '2px solid #dce0e5' }}>
        <div style={{ padding: '10px 12px' }} />
        {scale.map(opt => (
          <div key={opt.value} style={{ padding: '8px 2px', fontSize: '10px', textAlign: 'center', color: '#5a6572', fontWeight: 600, lineHeight: 1.2, whiteSpace: 'pre-line' }}>{opt.label}</div>
        ))}
      </div>
      {/* Item rows */}
      {items.map((item, ri) => {
        const answered = responses[item.id] != null;
        return (
          <div key={item.id} style={{
            display: 'grid', gridTemplateColumns: GRID_COLS, alignItems: 'center',
            background: ri % 2 ? '#ffffff' : '#fafbfc',
            borderTop: ri ? '1px solid #f0f1f3' : 'none',
            borderLeft: answered ? '3px solid #0066cc' : '3px solid transparent',
          }}>
            <div style={{ padding: '12px', fontSize: '14px', color: '#2d3748', lineHeight: 1.45 }}>{item.text}</div>
            {scale.map(opt => {
              const active = responses[item.id] === opt.value;
              return (
                <label key={opt.value} title={opt.label.replace('\n', ' ')} onClick={() => handleResponse(item.id, opt.value)}
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '12px 0', cursor: 'pointer', background: active ? '#e3f2fd' : 'transparent', alignSelf: 'stretch' }}>
                  <input type="radio" name={item.id} value={opt.value} checked={active}
                    onChange={() => handleResponse(item.id, opt.value)}
                    style={{ width: 17, height: 17, accentColor: '#0066cc', cursor: 'pointer', margin: 0 }} />
                </label>
              );
            })}
          </div>
        );
      })}
    </div>
    );
  };

  return (
    <div style={S.container}>
      {/* Progress bar */}
      <div style={S.bar}>
        <div style={{ ...S.bar, width: `${progress}%`, backgroundColor: '#0066cc', marginBottom: 0 }} />
      </div>
      <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '24px' }}>
        Section {currentSection + 1} of {totalSections}
      </div>

      {isLikertPage && section ? (
        <>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1a202c', marginBottom: '24px' }}>
            {section.title}
          </h3>
          {renderMatrix(section.items)}

          {/* Navigation */}
          <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            {currentSection > 0 && (
              <button onClick={() => setCurrentSection(p => p - 1)} style={{ ...S.btn, backgroundColor: '#6c757d', color: 'white' }}>
                Back
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button
              onClick={() => setCurrentSection(p => p + 1)}
              disabled={!sectionComplete}
              style={{ ...S.btn, backgroundColor: sectionComplete ? '#0066cc' : '#ccc', color: 'white', cursor: sectionComplete ? 'pointer' : 'not-allowed' }}
            >
              Next
            </button>
          </div>
        </>
      ) : (
        /* Final page: processing mode + structured open-ended (CB2 is now a normal
           Likert item in the "About the Content" section). */
        <>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1a202c', marginBottom: '24px' }}>
            Final Questions
          </h3>

          {/* Processing mode — self-reported gist/verbatim */}
          <div style={{ marginBottom: '28px' }}>
            <div style={S.q}>Which best describes how you evaluated this article?</div>
            <div style={{
              display: 'flex', flexDirection: 'column', gap: '8px',
              padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '2px solid #e9ecef',
            }}>
              {[
                { value: 'gist', label: 'I went with my overall impression of the article' },
                { value: 'verbatim', label: 'I carefully examined specific details and claims' },
                { value: 's1_to_s2', label: 'I started with my impression, then checked details' },
                { value: 's2_to_s1', label: 'I started checking details, then went with my gut feeling' },
              ].map(opt => (
                <label
                  key={opt.value}
                  onClick={() => setProcessingMode(opt.value)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 16px', backgroundColor: processingMode === opt.value ? '#e3f2fd' : 'white',
                    borderRadius: '6px', border: processingMode === opt.value ? '2px solid #0066cc' : '1px solid #dee2e6',
                    cursor: 'pointer', transition: 'all 0.2s ease',
                  }}
                >
                  <input
                    type="radio" name="processingMode" value={opt.value}
                    checked={processingMode === opt.value}
                    onChange={() => setProcessingMode(opt.value)}
                    style={{ margin: 0 }}
                  />
                  <span style={{ fontSize: '14px', color: '#2d3748', fontWeight: processingMode === opt.value ? 600 : 400 }}>
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Structured cue-attribution — how much each cue class influenced belief.
              Maps to the typology; see STUDY2_CODEBOOK. */}
          <div style={{ marginBottom: '28px' }}>
            <div style={S.q}>
              How much did each of the following influence how much you believed this article?
            </div>
            {renderMatrix(CUE_ATTRIBUTION, INFLUENCE)}
            <div style={{ fontSize: '12px', color: cueAttributionComplete ? '#28a745' : '#6c757d', marginTop: '8px' }}>
              {cueAttributionComplete ? '✓ Thanks' : 'Please rate each row.'}
            </div>
          </div>

          {/* Manipulation check — perceived political lean (bipolar scale) */}
          <div style={{ marginBottom: '28px' }}>
            <div style={S.q}>{PARTISAN_ITEM.text}</div>
            {renderMatrix([PARTISAN_ITEM], POLITICAL)}
          </div>

          {/* Manipulation check — endorser recognition (addresses Reviewer 3) */}
          <div style={{ marginBottom: '28px' }}>
            <div style={S.q}>Did this article include a post or endorsement from a public figure?</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '2px solid #e9ecef' }}>
              {ENDORSER_NOTICED_OPTIONS.map(opt => (
                <label key={opt.value} onClick={() => setEndorserNoticed(opt.value)}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
                    backgroundColor: endorserNoticed === opt.value ? '#e3f2fd' : 'white', borderRadius: '6px',
                    border: endorserNoticed === opt.value ? '2px solid #0066cc' : '1px solid #dee2e6', cursor: 'pointer' }}>
                  <input type="radio" name="endorserNoticed" value={opt.value} checked={endorserNoticed === opt.value}
                    onChange={() => setEndorserNoticed(opt.value)} style={{ margin: 0 }} />
                  <span style={{ fontSize: '14px', color: '#2d3748', fontWeight: endorserNoticed === opt.value ? 600 : 400 }}>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Prior-exposure control (illusory truth) — asked LAST, about the
              story/claim from any source, so it can't prime reading or ratings. */}
          <div style={{ marginBottom: '28px' }}>
            <div style={S.q}>Before today, had you already seen or heard about the story or claim in this article — from any source?</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '2px solid #e9ecef' }}>
              {PRIOR_EXPOSURE_OPTIONS.map(opt => (
                <label key={opt.value} onClick={() => setPriorExposure(opt.value)}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
                    backgroundColor: priorExposure === opt.value ? '#e3f2fd' : 'white', borderRadius: '6px',
                    border: priorExposure === opt.value ? '2px solid #0066cc' : '1px solid #dee2e6', cursor: 'pointer' }}>
                  <input type="radio" name="priorExposure" value={opt.value} checked={priorExposure === opt.value}
                    onChange={() => setPriorExposure(opt.value)} style={{ margin: 0 }} />
                  <span style={{ fontSize: '14px', color: '#2d3748', fontWeight: priorExposure === opt.value ? 600 : 400 }}>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Optional open-ended (original free-text, retained) */}
          <div style={{ marginBottom: '28px' }}>
            <div style={S.qLight}>
              Anything else that influenced your belief about this article? <span style={{ color: '#aaa' }}>(optional)</span>
            </div>
            <textarea
              value={openEnded}
              onChange={e => setOpenEnded(e.target.value)}
              placeholder="Describe what stood out to you..."
              style={{
                width: '100%', minHeight: '80px', padding: '12px 16px', fontSize: '15px',
                border: '2px solid #e9ecef', borderRadius: '8px', resize: 'vertical',
                fontFamily: 'inherit', lineHeight: 1.6,
              }}
            />
          </div>

          {/* Navigation */}
          <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <button onClick={() => setCurrentSection(p => p - 1)} style={{ ...S.btn, backgroundColor: '#6c757d', color: 'white' }}>
              Back
            </button>
            {(() => {
              const ready = !!processingMode && cueAttributionComplete && manipChecksComplete;
              return (
                <button
                  onClick={handleSubmit}
                  disabled={!ready}
                  style={{ ...S.btn, backgroundColor: ready ? '#28a745' : '#ccc', color: 'white', cursor: ready ? 'pointer' : 'not-allowed' }}
                >
                  Submit & Continue
                </button>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
};

export default PostArticleSurvey;
